<template>
  <div class="ubicacion-detail-page">
    <!-- Barra superior con título y acciones -->
    <v-card class="mb-4 elevation-2">
      <v-app-bar flat dense color="primary" dark>
        <v-btn icon @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>Detalles de Ubicación</v-toolbar-title>
        <v-spacer></v-spacer>

        <v-btn icon @click="showUbicacionForm(ubicacion)" v-if="!loading && canEditLocation">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </v-app-bar>
    </v-card>

    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="50" width="5"></v-progress-circular>
      <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-1">
        Cargando detalles de la ubicación...
      </div>
    </div>

    <template v-else>
      <v-container>
        <v-row>
          <!-- Tarjeta principal de información -->
          <v-col cols="12" md="7">
            <v-card class="elevation-1">
              <!-- Encabezado con nombre de ubicación -->
              <div class="location-header pa-4">
                <div class="d-flex align-center">
                  <v-avatar color="primary" size="52" class="mr-4">
                    <v-icon dark size="32">mdi-map-marker</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-h5 font-weight-bold">{{ ubicacion.name }}</div>
                    <div class="subtitle-1 grey--text text--darken-1">
                      {{ ubicacion.city }}, {{ ubicacion.state }}
                    </div>
                  </div>
                </div>
              </div>

              <v-divider></v-divider>

              <!-- Detalles de la ubicación -->
              <v-card-text class="py-4">
                <v-row>
                  <!-- Columna izquierda: Datos de ubicación -->
                  <v-col cols="12" lg="6">
                    <v-list dense class="transparent">
                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-office-building-marker</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Nombre</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">{{ ubicacion.name }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-map-marker-radius</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Dirección</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">{{ ubicacion.address }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-city</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Ciudad</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">{{ ubicacion.city }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-map</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Estado/Provincia</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">{{ ubicacion.state }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>

                  <!-- Columna derecha: Datos de contacto -->
                  <v-col cols="12" lg="6">
                    <v-list dense class="transparent">

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-mailbox</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Código Postal</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">{{ ubicacion.zip || 'No especificado'
                            }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-phone</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Teléfono</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a v-if="ubicacion.phone" :href="`tel:${ubicacion.phone}`" class="text-decoration-none">
                              {{ ubicacion.phone }}
                            </a>
                            <span v-else>No especificado</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-email</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Correo Electrónico</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a v-if="ubicacion.email" :href="`mailto:${ubicacion.email}`" class="text-decoration-none">
                              {{ ubicacion.email }}
                            </a>
                            <span v-else>No especificado</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <!-- Información del dealer (solo para colaboradores)
                      <v-list-item v-if="ubicacion.dealer && $store.getters.HAS_ROLE(['colaborator'])" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">{{ ubicacion.dealer.is_colaborador ? 'mdi-home' : 'mdi-store' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Dealer asignado</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1 d-flex align-center">
                            <v-avatar v-if="ubicacion.dealer.imageUrl" size="20" class="mr-2">
                              <img :src="ubicacion.dealer.imageUrl.url" alt="Logo dealer">
                            </v-avatar>
                            {{ ubicacion.dealer.name }}
                            <v-chip v-if="ubicacion.dealer.is_colaborador" x-small class="ml-2" color="primary">
                              Propio
                            </v-chip>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item> -->
                    </v-list>
                  </v-col>
                </v-row>

                <!-- Sección de Estados de Proceso -->
                <v-divider class="my-4"></v-divider>
                <div class="estados-proceso-section">
                  <div class="text-subtitle-1 font-weight-medium mb-3 d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-list-status</v-icon>
                    Estados de Proceso
                  </div>
                  
                  <div v-if="ubicacion.estadoProcesos && ubicacion.estadoProcesos.length > 0" class="d-flex flex-wrap">
                    <div 
                      v-for="estado in ubicacion.estadoProcesos"
                      :key="estado.id"
                      class="estado-circle-container mr-3 mb-3"
                    >
                      <div 
                        class="estado-circle"
                        :style="`background-color: ${estado.button_color}; color: ${estado.text_color};`"
                      >
                        <span class="estado-letra">{{ estado.name.charAt(0).toUpperCase() }}</span>
                      </div>
                      <div class="estado-tooltip">{{ estado.name }}</div>
                    </div>
                  </div>
                  
                  <div v-else class="text-center py-4">
                    <v-icon color="grey lighten-1" size="48">mdi-information-outline</v-icon>
                    <div class="text-subtitle-2 grey--text mt-2">No hay estados de proceso asignados</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <!-- Formulario de edición -->
    <ubicacion-form v-if="visibleUbicacionForm" v-model="editItem" @save="updateDataTable"
      @cancel="visibleUbicacionForm = false"></ubicacion-form>
  </div>
</template>

<script>
import UbicacionForm from './UbicacionForm.vue'
import LocationService from '@/services/LocationService'
import DealerService from '../../services/DealerService'
import { mapGetters } from 'vuex'

export default {
  components: {
    UbicacionForm
  },

  data () {
    return {
      loading: true,
      ubicacion: null,
      dealer_name: '',
      visibleUbicacionForm: false,
      editItem: null,

      // Estadísticas (valores de ejemplo, deberías obtenerlos de tu API)
      vehiculosCount: 0,
      entradasCount: 0,
      salidasCount: 0
    }
  },
  computed: {
    ...mapGetters(['HAS_PERMISSION']),
    canEditLocation () {
      return this.HAS_PERMISSION('editar_ubicacion')
    }
  },

  async created () {
    this.loading = true
    try {
      const { data } = await DealerService.get()
      this.dealer_name = data.data[0].name
    } catch (error) {
      console.error('Error al cargar el dealer:', error)
    }
    await this.loadUbicacionDetails()
    await this.loadEstadisticas()
  },

  methods: {
    async loadUbicacionDetails () {
      try {
        this.loading = true
        const ubicacionId = this.$route.params.ubicacion_id
        const { data } = await LocationService.view({ id: ubicacionId, expand: 'dealer' })
        this.ubicacion = data.data
      } catch (error) {
        console.error('Error al cargar los detalles de la ubicación:', error)
        this.$toast.error('Error al cargar los detalles de la ubicación')
      } finally {
        this.loading = false
      }
    },

    async loadEstadisticas () {
      try {
        // Esta es una implementación de ejemplo - deberías adaptarla a tus endpoints reales
        // const ubicacionId = this.$route.params.ubicacion_id

        // Ejemplo: contar vehículos en esta ubicación
        // const vehiculosResponse = await API.getVehiculosByLocation(ubicacionId)
        // this.vehiculosCount = vehiculosResponse.data.length
        this.vehiculosCount = 12 // Valor de ejemplo

        // Ejemplo: contar entradas/salidas en los últimos 30 días
        // const movimientosResponse = await MovimientoService.getByLocation(ubicacionId)
        // Procesar y contar entradas/salidas
        this.entradasCount = 8 // Valor de ejemplo
        this.salidasCount = 5 // Valor de ejemplo
      } catch (error) {
        console.error('Error al cargar estadísticas:', error)
      }
    },

    showUbicacionForm (item) {
      this.editItem = item
      this.visibleUbicacionForm = true
    },

    updateDataTable () {
      this.visibleUbicacionForm = false
      this.loadUbicacionDetails()
      this.$toast.success('Ubicación actualizada con éxito')
    }
  }
}
</script>

<style scoped>
.location-header {
  background-color: rgba(0, 0, 0, 0.01);
}

.map-placeholder {
  position: relative;
}

.map-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 100%);
}

/* Transiciones y efectos */
.v-card {
  transition: box-shadow 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

/* Estilos para Estados de Proceso */
.estados-proceso-section {
  padding: 16px 0;
}

.estado-circle-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.estado-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.estado-circle:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  border-color: rgba(0,0,0,0.2);
}

.estado-letra {
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
}

.estado-tooltip {
  font-size: 11px;
  margin-top: 4px;
  text-align: center;
  max-width: 60px;
  word-wrap: break-word;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.87);
}
</style>
